/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  AlertTriangle, 
  Clock, 
  Calendar, 
  FileCheck2, 
  ChevronRight, 
  Download, 
  X, 
  ShieldAlert, 
  Award, 
  ExternalLink,
  ChevronDown,
  Building2,
  FileSpreadsheet
} from 'lucide-react';
import { CertificatesAlertEvaluation } from '../utils/certificateAlertHelper.ts';
import { exportCertificatesToExcel } from '../utils/certificationsExcelExporter.ts';

interface CertificatesExpirationBannerProps {
  evaluation: CertificatesAlertEvaluation;
  onNavigateToCertificates?: () => void;
  compact?: boolean;
  variant?: 'banner' | 'card' | 'floating';
}

export const CertificatesExpirationBanner: React.FC<CertificatesExpirationBannerProps> = ({
  evaluation,
  onNavigateToCertificates,
  compact = false,
  variant = 'banner'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!evaluation.hasUrgentAlerts || isDismissed) {
    return null;
  }

  const { expiringCount, expiredCount, urgentTotalCount, urgentCertificates } = evaluation;

  const handleDownloadExcel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setIsDownloading(true);
      const certsToExport = urgentCertificates.map(u => u.certificate);
      await exportCertificatesToExcel(certsToExport, {
        reportTitle: 'REPORTE URGENTE: CERTIFICACIONES Y AUDITORÍAS POR VENCER (<30 DÍAS)',
        filterDescription: 'Certificaciones que requieren renovación inmediata o auditoría urgente',
        includeExpiringOnlySheet: true,
        highlightExpiringDays: 30
      });
    } catch (err) {
      console.error('Error downloading expiring certificates Excel:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const isSevere = expiredCount > 0;
  const primaryBg = isSevere 
    ? 'bg-gradient-to-r from-red-950/90 via-slate-900 to-red-950/80 border-red-500/50'
    : 'bg-gradient-to-r from-amber-950/90 via-slate-900 to-amber-950/80 border-amber-500/50';

  const accentColor = isSevere ? 'text-red-400' : 'text-amber-400';
  const badgeBg = isSevere ? 'bg-red-500/20 text-red-300 border-red-500/40' : 'bg-amber-500/20 text-amber-300 border-amber-500/40';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.99 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10 }}
      className={`rounded-2xl border p-4 sm:p-5 shadow-xl relative overflow-hidden transition-all ${primaryBg} ${
        variant === 'floating' ? 'fixed bottom-6 right-6 z-50 max-w-lg border-2 shadow-2xl backdrop-blur-md' : 'w-full mb-6'
      }`}
    >
      {/* Subtle glowing ambient background effect */}
      <div 
        className={`absolute -right-16 -top-16 w-48 h-48 rounded-full blur-3xl pointer-events-none opacity-20 ${
          isSevere ? 'bg-red-600' : 'bg-amber-500'
        }`}
      />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
        
        {/* Left icon and message */}
        <div className="flex items-start gap-3.5">
          <div className={`p-2.5 rounded-xl border flex items-center justify-center shrink-0 ${badgeBg} ${isSevere ? 'animate-pulse' : ''}`}>
            {isSevere ? (
              <ShieldAlert className="h-6 w-6 text-red-400" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-400" />
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-mono font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${badgeBg}`}>
                {isSevere ? '🚨 ALERTA CRÍTICA DE VENCIMIENTO' : '⚠️ AVISO PREVENTIVO: VENCIMIENTO < 30 DÍAS'}
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {urgentTotalCount} {urgentTotalCount === 1 ? 'documento detectado' : 'documentos detectados'}
              </span>
            </div>

            <h4 className="text-sm sm:text-base font-bold text-white tracking-tight flex items-center gap-2">
              <span>
                {isSevere 
                  ? `Atención HSEQ: Hay ${expiredCount} certificación(es) vencida(s) y ${expiringCount} por vencer en menos de 30 días.`
                  : `Atención HSEQ: Se detectaron ${expiringCount} certificación(es) que vencen en menos de 30 días.`}
              </span>
            </h4>

            <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
              Verificación automática ejecutada al inicio del sistema. Se requiere programar auditoría o actualizar documentación para mantener la vigencia operativa.
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 self-end sm:self-center shrink-0 flex-wrap">
          <button
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            className="px-3 py-2 rounded-xl text-xs font-bold bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs disabled:opacity-50"
            title="Descargar lista de certificaciones próximas a vencer en Excel con formato profesional"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-400" />
            <span>{isDownloading ? 'Generando...' : 'Descargar Excel'}</span>
          </button>

          {onNavigateToCertificates && (
            <button
              onClick={onNavigateToCertificates}
              className={`px-3.5 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer shadow-md text-white ${
                isSevere 
                  ? 'bg-red-600 hover:bg-red-500 shadow-red-900/30' 
                  : 'bg-amber-600 hover:bg-amber-500 shadow-amber-900/30'
              }`}
            >
              <span>Gestionar Certificaciones</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-2.5 py-2 rounded-xl text-xs font-medium text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-800 border border-slate-700 transition-all flex items-center gap-1 cursor-pointer"
            title={isExpanded ? 'Ocultar detalles' : 'Ver detalle de certificaciones'}
          >
            <span className="text-[11px]">{isExpanded ? 'Menos' : 'Detalles'}</span>
            <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          <button
            onClick={() => setIsDismissed(true)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/80 transition-all cursor-pointer"
            title="Descartar aviso temporalmente"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

      </div>

      {/* Expanded list of expiring certifications */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-slate-800 space-y-2 overflow-hidden"
          >
            <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono font-bold uppercase pb-1">
              <span>Listado de Documentos en Riesgo de Vencimiento:</span>
              <span>{urgentCertificates.length} items</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-60 overflow-y-auto pr-1">
              {urgentCertificates.map(({ certificate, validity, costCenterName }) => {
                const isExp = validity.computedStatus === 'vencido';
                return (
                  <div
                    key={certificate.id}
                    className={`p-3 rounded-xl border flex items-center justify-between gap-3 text-xs transition-all ${
                      isExp 
                        ? 'bg-red-950/40 border-red-500/30 text-red-200' 
                        : 'bg-amber-950/30 border-amber-500/30 text-amber-200'
                    }`}
                  >
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-bold text-[10px] px-1.5 py-0.2 rounded bg-slate-900 text-white border border-slate-700">
                          {certificate.code || 'CERT'}
                        </span>
                        <p className="font-bold text-white truncate">{certificate.name}</p>
                      </div>
                      <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-slate-500 shrink-0" />
                        <span>{costCenterName}</span>
                        <span>• Emisor: {certificate.issuer}</span>
                      </p>
                    </div>

                    <div className="text-right shrink-0">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-mono font-extrabold ${
                        isExp ? 'bg-red-500/30 text-red-300 border border-red-500/50' : 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                      }`}>
                        {isExp 
                          ? `VENCIDO (${Math.abs(validity.daysRemaining)}d)` 
                          : `VENCE EN ${validity.daysRemaining} DÍAS`}
                      </span>
                      <p className="text-[9px] text-slate-400 font-mono mt-0.5">
                        Límite: {certificate.validUntil}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
};
